// scrollScope — the router's scroll restoration keyed by the place the person is
// in, not by the history entry (FRONTEND-PATTERNS §Scroll doctrine). React
// Router's <ScrollRestoration> keys saved positions by location, so a new entry
// arrives with no saved position and scrolls to the top: the condensed bar
// dismounts and the nav returns. Two kinds of entry share the key of the place
// they belong to:
//   • an entity's tabs — an entity layout route declares `handle: { scrollScope:
//     'entity' }`; every sub-route under it shares the layout's matched pathname
//     as its key, so a sibling-tab switch keeps its scroll (the landing rule then
//     refines it to the working top);
//   • an overlay's step — the peek, the file viewer, the import run carry the
//     scroll key of the place they opened over in their step's mark
//     (lib/entryState), so opening, closing and coming back to one never moves
//     the page under it — for as long as the place under the overlay is still
//     the one it opened over. A list changed while the overlay was open (a page,
//     a filter — the desk's list stays live beside the sheet) is another view:
//     its scroll is its own, and the view it left keeps the scroll it had.
// Every other location keeps the default key — a list, a wizard.

import type { Location, UIMatch } from 'react-router-dom';
import { overlayStepOf, placeUnchanged } from './entryState.js';

export interface ScrollScopeHandle {
  scrollScope: 'entity';
}

/** The query a location shows. The live entry's is the window's: the list's own writes (nuqs, shallow) move the
 *  address without telling the router. */
function shownQuery(location: Location): string {
  const live = (window.history.state as { key?: string } | null)?.key === location.key;
  return live ? window.location.search : location.search;
}

export function scrollScopeKey(location: Location, matches: UIMatch[]): string {
  const scope = matches.find((m) => (m.handle as Partial<ScrollScopeHandle> | undefined)?.scrollScope === 'entity');
  if (scope) return scope.pathname;
  const step = overlayStepOf(location.state);
  return step && placeUnchanged(step, shownQuery(location)) ? step.scroll : location.key;
}
